/*
 * ----------------------------------------------------------------
 * 文件：src/api/request.js
 * 描述：Axios 实例封装，包含拦截器、基础配置等。
 * ----------------------------------------------------------------
 */
import axios from 'axios';
// 如果使用Element Plus或Ant Design Vue等UI库，可以引入它们的Message组件用于错误提示
// import { ElMessage } from 'element-plus';

// 1. 创建 axios 实例
const service = axios.create({
    // a. 基础URL，所有请求的公共部分。
    // 在 .env.development 文件中进行配置，方便不同环境的切换
    // 当不使用代理时，直接指向后端API地址
    baseURL: process.env.VUE_APP_BASE_API || 'https://img.xwxwxw.com.cn/member-api', // 请将此地址替换为你的实际后端API地址
    // b. 请求超时时间
    timeout: 10000, // 10秒
});

// 2. 请求拦截器
service.interceptors.request.use(
    config => {
        // 在发送请求前做些什么
        // 例如，从本地存储或Vuex/Pinia中获取token
        const token = localStorage.getItem('token');
        if (token) {
            // 让每个请求携带自定义token
            config.headers['Authorization'] = 'Bearer ' + token;
        }
        // 你也可以在这里添加全局的loading开启
        return config;
    },
    error => {
        // 对请求错误做些什么
        console.error('Request Error:', error); // for debug
        return Promise.reject(error);
    }
);

// 3. 响应拦截器
service.interceptors.response.use(
    /**
     * 如果你想获取诸如 headers 或 status 之类的http信息
     * 请直接 return  response
     */

    /**
     * 通过自定义代码确定请求状态
     * 这里只是一个例子，你也可以通过HTTP状态码来判断
     */
    response => {
        // 关闭全局 loading

        // a. 获取后端返回的原始数据
        const res = response.data;

        // b. 这里是核心判断，根据你和后端约定的业务状态码进行判断
        //    例如：如果业务状态码不是 0 (根据markdown)，就判定为错误。
        if (res.code !== 0) {
            // 使用UI库的Message组件进行提示
            // ElMessage({
            //   message: res.msg || 'Error',
            //   type: 'error',
            //   duration: 5 * 1000
            // });
            console.error('API Business Error:', res.msg);


            // 50008: 非法的token; 50012: 其他客户端登录了; 50014: Token 过期了;
            if (res.code === 50008 || res.code === 50012 || res.code === 50014) {
                // 可以做一些全局的token过期处理，例如：
                // MessageBox.confirm('您已登出，可以取消继续留在该页面，或者重新登录', '确定登出', {
                //   confirmButtonText: '重新登录',
                //   cancelButtonText: '取消',
                //   type: 'warning'
                // }).then(() => {
                //   store.dispatch('user/resetToken').then(() => {
                //     location.reload();
                //   })
                // })
                console.error('Token is invalid, redirect to login.');
            }

            return Promise.reject(new Error(res.msg || 'Error'));
        } else {
            // c. 如果业务状态码正确，则直接返回后端数据中的 `data` 部分
            return res.data;
        }
    },
    error => {
        // d. 处理HTTP网络错误
        console.error('HTTP Error:', error.message); // for debug
        // ElMessage({
        //   message: error.message,
        //   type: 'error',
        //   duration: 5 * 1000
        // });
        // 关闭全局 loading
        return Promise.reject(error);
    }
);

export default service;

/*
 * ----------------------------------------------------------------
 * 新增：官网内容相关API
 * 描述：根据提供的Markdown文档生成的API请求函数
 * ----------------------------------------------------------------
 */

/**
 * @description GET 获取所有产品列表
 * @param {object} params - 查询参数
 * @returns {Promise<object>} Promise resolves to a data object.
 * @example
 * {
 * "list": [
 * {
 * "id": 1,
 * "category_id": 7,
 * "product_code": "PD225A",
 * "name": "百奥白胖子_PD225A",
 * "cover_image_path": "images/e08d42ea7da7207ba4cdc35c8c83f488.jpg",
 * "detail_images_paths": ["..."],
 * "overview": "<p>...</p>",
 * "technical_parameters": { "净重": "9.0kgqqq", ... },
 * "application_fields": "<p>...</p>",
 * "application_scenarios": "<p>...</p>",
 * "related_materials": "<div>...</div>",
 * "show_application_fields": 1,
 * "show_application_scenarios": 1,
 * "show_related_materials": 0,
 * "sort_order": 10,
 * "is_active": 1,
 * "is_recommended": 0,
 * "created_at": "2025-06-22 20:33:04",
 * "updated_at": "2025-06-22 17:37:57"
 * }
 * ],
 * "page_info": { "pagesize": 20, "page": 1, "total": 4 }
 * }
 */
export function getProductList(params) {
    return service({
        url: '/product',
        method: 'get',
        params
    });
}

/**
 * @description GET 页面标语
 * @param {object} params - 查询参数, e.g., { banner_key: '售后服务' }
 * @returns {Promise<object>} Promise resolves to a data object.
 * @example
 * {
 * "list": [
 * {
 * "id": 1,
 * "banner_key": "售后服务",
 * "title": "售后服务",
 * "subtitle": "MAINTENANCE SERVICES",
 * "tagline": "...",
 * "features": "[{\"normal_text\": \"...\", \"strong_text\": \"...\"}]",
 * "is_active": 1,
 * "sort_order": 0,
 * "created_at": "2025-06-22 09:19:05",
 * "updated_at": "2025-06-22 09:19:05"
 * }
 * ],
 * "page_info": { "pagesize": 20, "page": 1, "total": 1 }
 * }
 */
export function getPageHeader(params) {
    return service({
        url: '/page-header',
        method: 'get',
        params
    });
}

/**
 * @description GET 产品分类
 * @returns {Promise<object>} Promise resolves to a data object.
 * @example
 * {
 * "list": [
 * {
 * "id": 1,
 * "parent_id": 0,
 * "name": "产品中心",
 * "parameter_template": null,
 * "sort_order": 10,
 * "created_at": "2025-06-22 20:32:49",
 * "updated_at": "2025-06-23 00:54:37"
 * }
 * ],
 * "page_info": { "pagesize": 20, "page": 1, "total": 16 }
 * }
 */
export function getProductsCategory(params) {
    return service({
        url: '/products-category',
        method: 'get',
        params // <--- 添加此行以传递参数
    });
}

/**
 * @description GET Banner
 * @returns {Promise<object>} Promise resolves to a data object.
 * @example
 * {
 * "list": [
 * {
 * "id": 1,
 * "title": "aaaaaaaaaaa",
 * "subtitle": "bbbbbbbbbbbbb",
 * "image_path": "images/...",
 * "link_url": null,
 * "link_text": null,
 * "platform": "all",
 * "sort_order": 0,
 * "is_active": 1,
 * "start_time": null,
 * "end_time": null,
 * "created_at": "2025-06-22 18:00:38",
 * "updated_at": "2025-06-22 18:00:38"
 * }
 * ],
 * "page_info": { "pagesize": 20, "page": 1, "total": 1 }
 * }
 */
export function getBanner() {
    return service({
        url: '/banner',
        method: 'get',
    });
}

/**
 * @description GET 资讯分类
 * @returns {Promise<object>} Promise resolves to a data object.
 * @example
 * {
 * "list": [
 * {
 * "id": 1,
 * "name": "企业资讯",
 * "slug": "corporate-news",
 * "sort_order": 10,
 * "created_at": "2025-06-23 02:18:35",
 * "updated_at": "2025-06-23 02:18:35"
 * }
 * ],
 * "page_info": { "pagesize": 20, "page": 1, "total": 5 }
 * }
 */
export function getNewsCategory(params) {
    return service({
        url: '/news-category',
        method: 'get',
        params
    });
}

/**
 * @description GET 资讯文章
 * @param {object} params - 查询参数
 * @returns {Promise<object>} Promise resolves to a data object.
 * @note The provided API documentation shows an empty object {} as the example response.
 * A typical response would likely follow the list/page_info structure.
 */
export function getNewsArticle(params) {
    return service({
        url: '/news-article',
        method: 'get',
        params
    });
}

/**
 * @description GET 应用优势表
 * @returns {Promise<object>} Promise resolves to a data object.
 * @example
 * {
 * "list": [
 * {
 * "id": 1,
 * "label": "防潮",
 * "icon_url": "files/u555.svg",
 * "sort_order": 10,
 * "is_active": 1,
 * "created_at": "2025-06-23 23:04:04",
 * "updated_at": "2025-06-23 16:38:34"
 * }
 * ],
 * "page_info": { "pagesize": 20, "page": 1, "total": 4 }
 * }
 */
export function getApplicationBenefit() {
    return service({
        url: '/application-benefit',
        method: 'get',
    });
}

/**
 * @description GET 行业应用表
 * @returns {Promise<object>} Promise resolves to a data object.
 * @example
 * {
 * "list": [
 * {
 * "id": 1,
 * "title": "车间厂房",
 * "subtitle": "工业生产环境",
 * "description": "...",
 * "humidity_range": "40% - 60% RH",
 * "temperature_range": "20°C - 30°C",
 * "image_url": "images/...",
 * "spokesperson": "工厂经理：",
 * "testimonials": "[{\"name\": \"陈\", \"quote\": \"...\"}]",
 * "sort_order": 10,
 * "is_active": 1,
 * "created_at": "2025-06-23 23:03:56",
 * "updated_at": "2025-06-23 16:32:34"
 * }
 * ],
 * "page_info": { "pagesize": 20, "page": 1, "total": 2 }
 * }
 */
export function getIndustryApplication() {
    return service({
        url: '/industry-application',
        method: 'get',
    });
}

/**
 * @description GET 简介页各模块静态内容
 * @returns {Promise<object>} Promise resolves to a data object.
 * @example
 * {
 * "list": [
 * {
 * "id": 1,
 * "section_key": "intro",
 * "title": "Waykar公司介绍",
 * "subtitle": "专业制造高品质家居产品...",
 * "content": "<h3>...</h3>",
 * "image_url": "/wimages/about/con2.jpg",
 * "updated_at": "2025-06-24 21:37:51"
 * }
 * ],
 * "page_info": { "pagesize": 20, "page": 1, "total": 7 }
 * }
 */
export function getProfileSection() {
    return service({
        url: '/profile-section',
        method: 'get',
    });
}

/**
 * @description GET 研发实力项
 * @returns {Promise<object>} Promise resolves to a data object.
 * @example
 * {
 * "list": [
 * {
 * "id": 1,
 * "item_type": "stat",
 * "title": "年研发投入",
 * "value": "1500万元",
 * "description": null,
 * "icon": "icon-investment",
 * "sort_order": 10,
 * "is_active": 1,
 * "created_at": "2025-06-24 21:37:54",
 * "updated_at": "2025-06-24 21:37:54"
 * }
 * ],
 * "page_info": { "pagesize": 20, "page": 1, "total": 10 }
 * }
 */
export function getProfileRdItem() {
    return service({
        url: '/profile-rd-item',
        method: 'get',
    });
}

/**
 * @description GET 发展历程里程碑
 * @returns {Promise<object>} Promise resolves to a data object.
 * @example
 * {
 * "list": [
 * {
 * "id": 1,
 * "year": "2006",
 * "title": "公司创立",
 * "description": "Waykar成立...",
 * "icon": "icon-foundation",
 * "achievements": "[\"创业起步\", \"团队组建\"]",
 * "sort_order": 10,
 * "is_active": 1,
 * "created_at": "2025-06-24 21:37:58",
 * "updated_at": "2025-06-24 21:37:58"
 * }
 * ],
 * "page_info": { "pagesize": 20, "page": 1, "total": 9 }
 * }
 */
export function getProfileMilestone(params) {
    return service({
        url: '/profile-milestone',
        method: 'get',
        params
    });
}

/**
 * @description GET 荣誉证书
 * @returns {Promise<object>} Promise resolves to a data object.
 * @example
 * {
 * "list": [
 * {
 * "id": 1,
 * "title": "ISO9001质量管理体系认证",
 * "description": "国际质量管理标准认证",
 * "year": "2017",
 * "image_url": "/wimages/about/4.png",
 * "sort_order": 10,
 * "is_active": 1,
 * "created_at": "2025-06-24 21:38:02",
 * "updated_at": "2025-06-24 21:38:02"
 * }
 * ],
 * "page_info": { "pagesize": 20, "page": 1, "total": 6 }
 * }
 */
export function getProfileHonor() {
    return service({
        url: '/profile-honor',
        method: 'get',
    });
}

/**
 * @description GET 销售网络项
 * @returns {Promise<object>} Promise resolves to a data object.
 * @example
 * {
 * "list": [
 * {
 * "id": 1,
 * "item_type": "point",
 * "title": "北美市场",
 * "value": null,
 * "description": "美国、加拿大销售网络",
 * "pos_x": "20.00",
 * "pos_y": "35.00",
 * "icon": null,
 * "sort_order": 10,
 * "is_active": 1
 * }
 * ],
 * "page_info": { "pagesize": 20, "page": 1, "total": 9 }
 * }
 */
export function getProfileNetworkItem() {
    return service({
        url: '/profile-network-item',
        method: 'get',
    });
}

/**
 * @description GET 企业文化价值观
 * @returns {Promise<object>} Promise resolves to a data object.
 * @example
 * {
 * "list": [
 * {
 * "id": 1,
 * "title": "诚信",
 * "description": "以诚待人...",
 * "icon": "icon-integrity",
 * "details": "[\"诚实守信...\", ...]",
 * "sort_order": 10,
 * "is_active": 1
 * }
 * ],
 * "page_info": { "pagesize": 20, "page": 1, "total": 4 }
 * }
 */
export function getProfileCultureValue() {
    return service({
        url: '/profile-culture-value',
        method: 'get',
    });
}

/**
 * @description GET 未来愿景目标
 * @returns {Promise<object>} Promise resolves to a data object.
 * @example
 * {
 * "list": [
 * {
 * "id": 1,
 * "title": "全球品牌",
 * "description": "成为全球家居环境产品领域的知名品牌",
 * "timeline": "2025-2027",
 * "icon": "icon-global-brand",
 * "sort_order": 10,
 * "is_active": 1
 * }
 * ],
 * "page_info": { "pagesize": 20, "page": 1, "total": 5 }
 * }
 */
export function getProfileVisionGoal() {
    return service({
        url: '/profile-vision-goal',
        method: 'get',
    });
}

/**
 * @description GET 案例分类表
 * @returns {Promise<object>} Promise resolves to a data object.
 * @example
 * {
 * "list": [
 * {
 * "id": 1,
 * "name": "电子行业方案",
 * "sort_order": 10,
 * "is_active": 1,
 * "created_at": "2025-06-25 00:20:44",
 * "updated_at": "2025-06-25 00:20:44"
 * }
 * ],
 * "page_info": { "pagesize": 20, "page": 1, "total": 7 }
 * }
 */
export function getCaseCategory() {
    return service({
        url: '/case-category',
        method: 'get',
    });
}

/**
 * @description GET 案例场景表
 * @param {object} params - 查询参数
 * @returns {Promise<object>} Promise resolves to a data object.
 * @example
 * {
 * "list": [
 * {
 * "id": 1,
 * "category_id": 1,
 * "title": "精密电子车间防静电除湿",
 * "description": "...",
 * "cover_image_path": "/wimages/...",
 * "problem_description_html": "<p>...</p>",
 * "solution_description_html": "<p>...</p>",
 * "solution_image_path": null,
 * "sort_order": 10,
 * "is_active": 1,
 * "created_at": "2025-06-25 00:20:50",
 * "updated_at": "2025-06-25 00:20:50"
 * }
 * ],
 * "page_info": { "pagesize": 20, "page": 1, "total": 4 }
 * }
 */
export function getCaseScenario(params) {
    return service({
        url: '/case-scenario',
        method: 'get',
        params
    });
}

/**
 * @description GET 服务-FAQ分类表
 * @returns {Promise<object>} Promise resolves to a data object.
 * @example
 * {
 * "list": [
 * {
 * "id": 1,
 * "name": "除湿产品",
 * "sort_order": 10,
 * "is_active": 1,
 * "created_at": "2025-06-25 00:50:08",
 * "updated_at": "2025-06-25 00:50:08"
 * }
 * ],
 * "page_info": { "pagesize": 20, "page": 1, "total": 5 }
 * }
 */
export function getServiceFaqCategory(params) {
    return service({
        url: '/service-faq-category',
        method: 'get',
        params
    });
}

/**
 * @description GET 服务-FAQ内容表
 * @param {object} params - 查询参数
 * @returns {Promise<object>} Promise resolves to a data object.
 * @example
 * {
 * "list": [
 * {
 * "id": 1,
 * "category_id": 1,
 * "question": "E5 除湿 制冷系统高压保护",
 * "answer": "检修方法：<br>1. ...",
 * "sort_order": 10,
 * "is_active": 1,
 * "created_at": "2025-06-25 00:50:15",
 * "updated_at": "2025-06-25 00:50:15"
 * }
 * ],
 * "page_info": { "pagesize": 20, "page": 1, "total": 5 }
 * }
 */
export function getServiceFaq(params) {
    return service({
        url: '/service-faq',
        method: 'get',
        params
    });
}

/**
 * @description GET 服务-服务规范表
 * @returns {Promise<object>} Promise resolves to a data object.
 * @example
 * {
 * "list": [
 * {
 * "id": 1,
 * "title": "一、产品保修说明",
 * "clauses": "[\"1.1 ...\", \"1.2 ...\"]",
 * "sort_order": 10,
 * "is_active": 1,
 * "created_at": "2025-06-25 00:50:27",
 * "updated_at": "2025-06-25 00:50:27"
 * }
 * ],
 * "page_info": { "pagesize": 20, "page": 1, "total": 1 }
 * }
 */
export function getServicePolicy() {
    return service({
        url: '/service-policy',
        method: 'get',
    });
}


/**
 * @description GET 公司亮点关键指标表
 * @returns {Promise<object>} Promise resolves to a data object.
 * @example
 * {
 *     "list": [
 *         {
 *             "id": 1,
 *             "label": "成立至今",
 *             "stat_value": "10",
 *             "stat_unit": "年",
 *             "icon_class": null,
 *             "sort_order": 10,
 *             "is_active": 1,
 *             "created_at": "2025-06-23 02:09:37",
 *             "updated_at": "2025-06-23 02:09:37"
 *         },
 *         {
 *             "id": 2,
 *             "label": "分布国家",
 *             "stat_value": "10",
 *             "stat_unit": "+",
 *             "icon_class": null,
 *             "sort_order": 20,
 *             "is_active": 1,
 *             "created_at": "2025-06-23 02:09:37",
 *             "updated_at": "2025-06-23 02:09:37"
 *         },
 *         {
 *             "id": 3,
 *             "label": "可回收",
 *             "stat_value": "100",
 *             "stat_unit": "%",
 *             "icon_class": null,
 *             "sort_order": 30,
 *             "is_active": 1,
 *             "created_at": "2025-06-23 02:09:37",
 *             "updated_at": "2025-06-23 02:09:37"
 *         },
 *         {
 *             "id": 4,
 *             "label": "可降解",
 *             "stat_value": "100",
 *             "stat_unit": "%",
 *             "icon_class": null,
 *             "sort_order": 40,
 *             "is_active": 1,
 *             "created_at": "2025-06-23 02:09:37",
 *             "updated_at": "2025-06-23 02:09:37"
 *         },
 *         {
 *             "id": 5,
 *             "label": "已服务家庭数量",
 *             "stat_value": "200万",
 *             "stat_unit": "+",
 *             "icon_class": null,
 *             "sort_order": 50,
 *             "is_active": 1,
 *             "created_at": "2025-06-23 02:09:37",
 *             "updated_at": "2025-06-23 02:09:37"
 *         }
 *     ],
 *     "page_info": {
 *         "pagesize": 20,
 *         "page": 1,
 *         "total": 5
 *     }
 * }
 */
export function getKeyMetric() {
    return service({
        url: '/key-metric',
        method: 'get',
    });
}


/**
 * @description GET 获取每个分类的精选产品
 * @returns {Promise<object>} Promise resolves to a data object.
 * @example
 * {
 * "list": [
 * {
 * "id": 1,
 * "category_id": 7,
 * "name": "百奥白胖子_PD225A",
 * "cover_image_path": "images/e08d42ea7da7207ba4cdc35c8c83f488.jpg",
 * "technical_parameters": { "净重": "9.0kg", ... },
 * "sort_order": 10,
 * "is_recommended": 0,
 * "created_at": "2025-06-22 20:33:04",
 * "updated_at": "2025-06-22 17:37:57"
 * }
 * ],
 * "page_info": { "pagesize": 20, "page": 1, "total": 4 }
 * }
 */
export function getFeaturedProducts() {
    return service({
        url: '/featured-products',
        method: 'get'
    });
}


/**
 * @description GET 获取每个分类的精选产品
 * @returns {Promise<object>} Promise resolves to a data object.
 * @example
 * {
 * "list": [
 * {
 * "id": 1,
 * "name": "联系方式",
 * "label": "联系方式",
 * "type": 1,
 * "value_string": "400-000-0000",
 * "value_image": [],
 * "value_text": null,
 * "value_json": {
 * "示例key": "示例value"
 * },
 * "created_at": "2025-07-06 15:33:01",
 * "updated_at": "2025-07-06 15:33:01",
 * "value_url": []
 * }
 * ],
 * "page_info": { "pagesize": 20, "page": 1, "total": 4 }
 * }
 */
export function getSetting() {
    return service({
        url: '/waykar-setting',
        method: 'get'
    });
}